import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import { useClipboard } from "@galaxy-io/dls/hooks/useClipboard";
import { InputSize, InputVariant } from "@galaxy-io/dls/inputs/Input";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import Box from "@galaxy-io/dls/layout/Box";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import { MenuItem, MenuItemVariant } from "@galaxy-io/dls/menu/Menu";
import Tabs from "@galaxy-io/dls/navigation/Tabs";
import Span from "@galaxy-io/dls/text/Span";
import Text, { TextSize, TextVariant, TextWeight } from "@galaxy-io/dls/text/Text";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import Widget from "@galaxy-io/dls/widget/Widget";
import type { WorkspaceMember, WorkspaceRole } from "@gtm/contracts";
import {
  BuildingsIcon,
  CopyIcon,
  PencilSimpleIcon,
  UserPlusIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useRouter, useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmAction } from "@/components/workspace/confirm-action";
import { DrawerList } from "@/components/workspace/drawer-content";
import { Inline, Stack } from "@/components/workspace/people-styles";
import { RecordAudit } from "@/components/workspace/record-audit";
import { RecordTable } from "@/components/workspace/record-table";
import { SelectField } from "@/components/workspace/select-field";
import { Status } from "@/components/workspace/status";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { BaseToolbar } from "@/components/workspace/toolbar";
import { useDialogReset } from "@/components/workspace/use-dialog-reset";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { SettingsActions, SettingsSection } from "./settings-header";

const roles = [
  { value: "member", label: "Member" },
  { value: "admin", label: "Admin" },
];
export function MembersView({
  currentUserId,
  workspaceName,
}: {
  currentUserId: string;
  workspaceName: string;
}) {
  const { params, update } = useUrlState();
  const tab = params.get("tab") === "invitations" ? "invitations" : "members";
  const [search, setSearch] = useState("");
  const [inviteSearch, setInviteSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const cache = useQueryClient(),
    router = useRouter();
  const members = useQuery({
    queryKey: ["workspace-members"],
    queryFn: () => unwrap(apiClient().GET("/organization/members")),
  });
  const invitations = useQuery({
    queryKey: ["workspace-invitations"],
    queryFn: () => unwrap(apiClient().GET("/organization/invitations")),
  });
  const selectedMember = members.data?.find((m) => m.userId === params.get("memberId"));
  const editing = useRetainedValue(selectedMember);
  const [revokingInvite, setRevokingInvite] = useState<{ id: string; email: string } | null>(null);
  async function act<T>(
    fn: () => Promise<T>,
    header: string,
    subheader: string,
    propagate = false,
  ) {
    setBusy(true);
    try {
      const result = await fn();
      await cache.invalidateQueries({ queryKey: ["workspace-members"] });
      await cache.invalidateQueries({ queryKey: ["workspace-invitations"] });
      router.refresh();
      toast({
        variant: ToastVariant.SUCCESS,
        header,
        description: subheader,
        ...toastDefaults(ToastVariant.SUCCESS),
      });
      return result;
    } catch (e) {
      if (propagate) throw e;
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not update workspace",
        description: (e as Error).message,
        ...toastDefaults(ToastVariant.ERROR),
      });
    } finally {
      setBusy(false);
    }
  }
  const filtered = (members.data || []).filter((m) =>
    `${m.name} ${m.email}`.toLowerCase().includes(search.toLowerCase()),
  );
  const invites = (invitations.data || []).filter((i) =>
    i.email.toLowerCase().includes(inviteSearch.toLowerCase()),
  );
  return (
    <>
      <ConfirmAction
        isOpen={!!revokingInvite}
        onOpenChange={(open) => {
          if (!open) setRevokingInvite(null);
        }}
        header="Revoke invitation?"
        description={`The invitation link for ${revokingInvite?.email ?? "this person"} will stop working.`}
        label="Revoke invitation"
        isDestructive
        onConfirm={async () => {
          if (revokingInvite)
            await act(
              () =>
                unwrap(
                  apiClient().DELETE("/organization/invitations/{id}", {
                    params: { path: { id: revokingInvite.id } },
                  }),
                ),
              "Invitation revoked",
              "The invitation link no longer works.",
              true,
            );
        }}
      />

      <SettingsSection id="workspace" title="Workspace">
        <WorkspaceName
          key={workspaceName}
          name={workspaceName}
          busy={busy}
          onSave={async (name) => {
            const result = await act(
              () => unwrap(apiClient().PATCH("/organization/settings", { body: { name } })),
              "Workspace updated",
              `Everyone now sees it as ${name}.`,
            );
            return !!result;
          }}
        />
      </SettingsSection>
      <SettingsSection id="members" title="Members">
        <Widget
          header="Team access"
          subheader="Manage the people who can access this workspace."
          icon={UsersIcon}
          isFlush
          gap={0}
          actions={
            <Button
              label="Invite member"
              icon={UserPlusIcon}
              size={ButtonSize.SMALL}
              onClick={() => update({ invite: "new" })}
            />
          }
        >
          <Stack>
            <Box padding={[0, 12]}>
              <Tabs
                ariaLabel="Member lists"
                value={tab}
                onChange={(value) => update({ tab: value === "members" ? null : value })}
                items={[
                  { id: "members", label: "Members" },
                  { id: "invitations", label: "Invitations" },
                ]}
              />
            </Box>
          </Stack>
          {tab === "members" ? (
            <>
              <BaseToolbar
                leadingActions={
                  <Input
                    aria-label="Search members"
                    placeholder="Search members…"
                    value={search}
                    onChange={(e) => setSearch(e)}
                    type="search"
                    size={InputSize.SMALL}
                    label="Search members"
                    hideLabel
                  />
                }
              />
              <RecordTable
                rowActions={(row) => row.actions}
                fillHeight={false}
                label="Members"
                emptyState={
                  <EmptyState
                    header={search ? "No matching members" : "No members yet"}
                    description={
                      search
                        ? "Try a different name or email."
                        : "Invite someone to join your workspace."
                    }
                  />
                }
                auditKind="member"
                isLoading={members.isPending}
                error={members.error || undefined}
                onRetry={() => void members.refetch()}
                rowKeys={filtered.map((m) => m.userId)}
                rows={filtered.map((m) => ({
                  name: (
                    <Span weight={TextWeight.MEDIUM}>
                      {m.name}
                      {m.userId === currentUserId ? " (you)" : ""}
                    </Span>
                  ),
                  email: m.email || "—",
                  kind: m.kind === "service" ? "Service account" : "User",
                  role: m.role === "admin" ? "Admin" : "Member",
                  status: <Status value={m.active ? "active" : "disabled"} />,
                  actions:
                    m.kind === "human" ? (
                      <MenuItem
                        label="Edit"
                        icon={PencilSimpleIcon}
                        onSelect={() => update({ memberId: m.userId })}
                      />
                    ) : null,
                }))}
                columns={[
                  { key: "name", label: "Name", width: "auto", pin: "left" },
                  { key: "email", label: "Email", width: "auto" },
                  { key: "kind", label: "Account type", width: 140 },
                  { key: "role", label: "Role", width: 100 },
                  { key: "status", label: "Status", width: 120 },
                ]}
              />
            </>
          ) : (
            <>
              <BaseToolbar
                leadingActions={
                  <Input
                    aria-label="Search invitations"
                    placeholder="Search invitations…"
                    value={inviteSearch}
                    onChange={(event) => setInviteSearch(event)}
                    type="search"
                    size={InputSize.SMALL}
                    label="Search invitations"
                    hideLabel
                  />
                }
              />
              <RecordTable
                rowActions={(row) => row.actions}
                fillHeight={false}
                label="Invitations"
                emptyState={
                  <EmptyState
                    header={inviteSearch ? "No matching invitations" : "No invitations yet"}
                    description={
                      inviteSearch
                        ? "Try a different email."
                        : "Invite a member to give them access to your workspace."
                    }
                  />
                }
                auditKind="invitation"
                isLoading={invitations.isPending}
                error={invitations.error || undefined}
                onRetry={() => void invitations.refetch()}
                rowKeys={invites.map((i) => i.id)}
                columns={[
                  { key: "email", label: "Email", width: "auto", pin: "left" },
                  { key: "role", label: "Role", width: 100 },
                  { key: "status", label: "Status", width: 140 },
                  { key: "expires", label: "Expires", width: 180 },
                ]}
                rows={invites.map((i) => {
                  const pending =
                    !i.acceptedAt && !i.revokedAt && new Date(i.expiresAt) > new Date();
                  return {
                    email: i.email,
                    role: i.role === "admin" ? "Admin" : "Member",
                    status: (
                      <Status
                        value={
                          i.acceptedAt
                            ? "active"
                            : i.revokedAt
                              ? "revoked"
                              : pending
                                ? "pending"
                                : "expired"
                        }
                        label={i.acceptedAt ? "Accepted" : undefined}
                      />
                    ),
                    expires: new Date(i.expiresAt).toLocaleString(),
                    actions: pending ? (
                      <MenuItem
                        label="Revoke"
                        variant={MenuItemVariant.ERROR}
                        isDisabled={busy}
                        onSelect={() => setRevokingInvite({ id: i.id, email: i.email })}
                      />
                    ) : null,
                  };
                })}
              />
            </>
          )}
        </Widget>
      </SettingsSection>
      {
        <InviteModal
          open={params.get("invite") === "new"}
          onClose={() => update({ invite: null })}
          onCreate={(email, role) =>
            act(
              () =>
                unwrap(
                  apiClient().POST("/organization/invitations", {
                    body: { email, role },
                  }),
                ),
              "Invitation link created",
              `Copy the link and share it with ${email}.`,
            )
          }
          busy={busy}
        />
      }
      {editing && (
        <MemberModal
          open={!!selectedMember}
          key={editing.userId}
          member={editing}
          busy={busy}
          onClose={() => update({ memberId: null })}
          onRemove={async () => {
            await act(
              () =>
                unwrap(
                  apiClient().DELETE("/organization/members/{id}", {
                    params: { path: { id: editing.userId } },
                  }),
                ),
              "Member removed",
              `${editing.name || editing.email || "They"} no longer has access.`,
              true,
            );
            update({ memberId: null });
          }}
          onSave={async (role, active) => {
            const result = await act(
              () =>
                unwrap(
                  apiClient().PATCH("/organization/members/{id}", {
                    params: { path: { id: editing.userId } },
                    body: { role, active },
                  }),
                ),
              "Member access updated",
              `Changes apply to ${editing.name || editing.email || "this member"}.`,
            );
            if (result) {
              update({ memberId: null });
              if (editing.userId === currentUserId && (!active || role !== "admin"))
                router.push("/");
            }
          }}
        />
      )}
    </>
  );
}
function InviteModal({
  open,
  onClose,
  onCreate,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (
    email: string,
    role: WorkspaceRole,
  ) => Promise<{ id: string; url: string; emailed?: boolean } | undefined>;
  busy: boolean;
}) {
  const [email, setEmail] = useState(""),
    [role, setRole] = useState<WorkspaceRole>("member"),
    [url, setUrl] = useState(""),
    // Whether the invitation was actually delivered. The link is shown either
    // way, because it is the invitation; this only decides what to say about it.
    [emailed, setEmailed] = useState(false);
  const { toast } = useToast();
  const clipboard = useClipboard();
  async function copy() {
    if (await clipboard.copy(url)) {
      toast({
        variant: ToastVariant.SUCCESS,
        header: "Invite link copied",
        description: "Share it with the person you invited.",
        ...toastDefaults(ToastVariant.SUCCESS),
      });
    } else {
      toast({
        variant: ToastVariant.ERROR,
        header: "Could not copy invite link",
        description: "Select the link below and copy it manually.",
        ...toastDefaults(ToastVariant.ERROR),
      });
    }
  }
  useDialogReset(open, () => {
    setEmail("");
    setRole("member");
    setUrl("");
    setEmailed(false);
  });
  return (
    <SurfaceDialog
      open={open}
      title={url ? "Invitation ready" : "Invite member"}
      onClose={onClose}
      busy={busy}
      footer={
        url ? (
          <Button label="Done" onClick={onClose} />
        ) : (
          <Button
            label="Create invite link"
            isLoading={busy}
            isDisabled={!email.trim()}
            onClick={async () => {
              const invite = await onCreate(email.trim().toLowerCase(), role);
              if (invite) {
                setUrl(invite.url);
                setEmailed(invite.emailed === true);
              }
            }}
          />
        )
      }
    >
      {url ? (
        <Stack>
          <Text>
            {emailed
              ? `Sent to ${email}. The link below is the same one, if you would rather pass it on yourself. It expires in 7 days.`
              : `Share this link with ${email}. It expires in 7 days.`}
          </Text>
          <Input
            aria-label="Invitation link"
            value={url}
            readOnly
            onFocus={(e) => e.target.select()}
            label="Invitation link"
          />
          <Button
            label="Copy invite link"
            icon={CopyIcon}
            variant={ButtonVariant.SECONDARY}
            onClick={() => void copy()}
          />
          <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
            Only an account with this verified email can accept.
            {emailed ? "" : " No email was sent, so the link has to be passed on by hand."}
          </Text>
        </Stack>
      ) : (
        <Stack>
          <TextInput
            label="Email address"
            ref={(el) => el?.setAttribute("aria-label", "Email address")}
            value={email}
            onChange={setEmail}
            placeholder="teammate@company.com"
            variant={InputVariant.BASE}
            fillWidth
          />
          <SelectField
            label="Role"
            value={role}
            options={roles}
            onChange={(v) => setRole(v as WorkspaceRole)}
          />
          <Text size={TextSize.BODY_SM} variant={TextVariant.SECONDARY}>
            {role === "admin"
              ? "Admins manage members, integrations, and workspace settings."
              : "Members can explore data, run workflows, and send records."}
          </Text>
        </Stack>
      )}
    </SurfaceDialog>
  );
}
function MemberModal({
  open,
  member,
  busy,
  onSave,
  onRemove,
  onClose,
}: {
  open: boolean;
  member: WorkspaceMember;
  busy: boolean;
  onSave: (role: WorkspaceRole, active: boolean) => Promise<void>;
  onRemove: () => Promise<void>;
  onClose: () => void;
}) {
  const [role, setRole] = useState(member.role),
    [active, setActive] = useState(member.active),
    // Removal is irreversible from here — they have to be invited again — so it
    // asks first, in place, rather than behind a dialog on a dialog.
    [confirming, setConfirming] = useState(false);
  useDialogReset(open, () => {
    setRole(member.role);
    setActive(member.active);
    setConfirming(false);
  });
  return (
    <>
      <ConfirmAction
        isOpen={confirming}
        onOpenChange={setConfirming}
        header="Remove member?"
        description={`${member.name || member.email} loses access immediately and needs a new invitation to return. Existing history stays attributed to them.`}
        label="Remove member"
        isDestructive
        onConfirm={onRemove}
      />
      <SurfaceDialog
        open={open}
        title="Member access"
        onClose={onClose}
        busy={busy}
        footer={
          <Inline>
            <Button
              label="Save changes"
              isLoading={busy}
              onClick={() => void onSave(role, active)}
            />
            <Button
              label="Remove member"
              variant={ButtonVariant.ERROR}
              onClick={() => setConfirming(true)}
            />
          </Inline>
        }
      >
        <DrawerList>
          <DescriptionList
            items={[
              { label: "Name", value: member.name },
              { label: "Email", value: member.email },
            ]}
          />
        </DrawerList>
        <RecordAudit kind="member" id={member.userId} />
        <SelectField
          label="Role"
          value={role}
          onChange={(v) => setRole(v as WorkspaceRole)}
          options={roles}
        />
        <SelectField
          label="Workspace access"
          value={active ? "active" : "disabled"}
          onChange={(v) => setActive(v === "active")}
          options={[
            { value: "active", label: "Active" },
            { value: "disabled", label: "Disabled" },
          ]}
        />
      </SurfaceDialog>
    </>
  );
}
function WorkspaceName({
  name,
  busy,
  onSave,
}: {
  name: string;
  busy: boolean;
  onSave: (name: string) => Promise<boolean>;
}) {
  const [value, setValue] = useState(name);
  const [saved, setSaved] = useState(name);
  const trimmed = value.trim();
  return (
    <Widget
      header="Workspace details"
      subheader="The name your team sees throughout GTM."
      icon={BuildingsIcon}
      footer={
        <SettingsActions>
          <Button
            label="Save changes"
            size={ButtonSize.SMALL}
            isDisabled={busy || !trimmed || trimmed === saved}
            isLoading={busy}
            onClick={async () => {
              if (await onSave(trimmed)) {
                setSaved(trimmed);
                setValue(trimmed);
              }
            }}
          />
          {trimmed !== saved && (
            <Button
              label="Cancel"
              variant={ButtonVariant.SECONDARY}
              isDisabled={busy}
              onClick={() => setValue(saved)}
            />
          )}
        </SettingsActions>
      }
    >
      <Box maxWidth={640}>
        <TextInput
          label="Workspace name"
          ref={(el) => el?.setAttribute("aria-label", "Workspace name")}
          value={value}
          onChange={setValue}
          isDisabled={busy}
          variant={InputVariant.BASE}
          fillWidth
        />
      </Box>
    </Widget>
  );
}
